import { z } from 'zod';

import type { Cents } from '@/shared/format/money';

// Fio da CreditAPI para a Operação de crédito (U27). Tipos do legado em
// services/creditNote/types/creditNoteReadModel.ts; discovery/02 §7.3. Leitura é `looseObject`: o que a
// API manda a mais passa; o que a tela usa está declarado.

/** Os 23 status da operação (discovery/02 §5.1), valor técnico da API. */
export const creditNoteStatusSchema = z.enum([
  'Draft',
  'Revision',
  'Disapproved',
  'Error',
  'ComplianceApproval',
  'CreditApproval',
  'InstrumentApproval',
  'MarginReserveApproval',
  'VehicleInspection',
  'VehicleInspectionApproval',
  'Signatures',
  'SignaturesValidation',
  'PartnerSignaturesValidation',
  'Warranty',
  'ManualWarranty',
  'WarrantyRevision',
  'WaitLiquidation',
  'ManualLiquidation',
  'Liquidation',
  'PaymentRevision',
  'OpenAccountRevision',
  'Finished',
  'Canceled',
]);
export type CreditNoteStatus = z.infer<typeof creditNoteStatusSchema>;
export const CREDIT_NOTE_STATUSES: readonly CreditNoteStatus[] = creditNoteStatusSchema.options;

const cents = z.number() as z.ZodType<Cents>;
const nullishString = z.string().nullish();

export const actorSchema = z.looseObject({
  userId: nullishString,
  userIdDisplay: nullishString,
  tenant: nullishString,
  tenantDisplay: nullishString,
});

export const ownerGroupSchema = z.looseObject({
  groupId: nullishString,
  groupDisplay: nullishString,
  tenant: nullishString,
  tenantDisplay: nullishString,
});

/** `TimelineType` (contexts/apiRequestContext.tsx:251): etapa da aba Histórico e do stepper. */
export const timelineItemSchema = z.looseObject({
  index: z.number(),
  name: z.string(),
  description: nullishString,
  startedAt: nullishString,
  finishedAt: nullishString,
  createdBy: actorSchema.nullish(),
  updatedBy: actorSchema.nullish(),
});
export type TimelineItem = z.infer<typeof timelineItemSchema>;

/** `IUploadReadModel`: `fileType` `Draft` (contrato), `Assignment` (cessão), `…SignedContract…`. */
export const uploadSchema = z.looseObject({
  id: nullishString,
  fileName: nullishString,
  fileType: z.string(),
  displayName: nullishString,
  tempUrl: nullishString,
  tempGetUrl: nullishString,
  tempPutUrl: nullishString,
  signatureWorkFlowId: nullishString,
  createdAt: nullishString,
});
export type Upload = z.infer<typeof uploadSchema>;

export const relatedPersonSchema = z.looseObject({
  id: nullishString,
  personId: z.string(),
  personIdDisplay: z.string().nullish().meta({ pii: 'name' }),
  discriminator: nullishString,
  typeOfRelationship: nullishString,
  typeOfRelationshipDisplay: nullishString,
  relatedToId: nullishString,
  relatedToIdDisplay: nullishString,
  signatureType: nullishString,
  /** O legado lista o `*Display` (InvolvedList.tsx:98-113), não o valor técnico. */
  signatureTypeDisplay: nullishString,
  signatureValidation: nullishString,
  signatureValidationDisplay: nullishString,
  /** As abas KYC e Crédito leem `personDiscriminator || discriminator` (Kyc/index.tsx:72). */
  personDiscriminator: nullishString,
  percentage: z.number().nullish(),
  isCreditBureauCheckSkipped: z.boolean().nullish(),
});
export type RelatedPerson = z.infer<typeof relatedPersonSchema>;

export const paymentScheduleItemSchema = z.looseObject({
  id: nullishString,
  paymentNo: z.number().nullish(),
  dueDate: z.string(),
  principalAmountInCents: cents,
  amortization: cents,
  interest: cents,
  financeTax: cents.nullish(),
  payment: cents,
  accruedInterest: cents.nullish(),
  addedFixedCosts: cents.nullish(),
});

/** `AmortizationReadModel` (discovery/02 §7.3): valores em centavos, `apr` em fração (0.0249). */
export const amortizationSchema = z.looseObject({
  id: nullishString,
  amortizationType: nullishString,
  calculateByValueType: nullishString,
  requestedAmount: cents.nullish(),
  principalAmountInCents: cents.nullish(),
  liquidValue: cents.nullish(),
  initialValue: cents.nullish(),
  startDate: nullishString,
  firstPaymentDate: nullishString,
  dueDate: nullishString,
  termInMonths: z.number().nullish(),
  numberOfPayments: z.number().nullish(),
  apr: z.number().nullish(),
  /** O prazo do legado sai da periodicidade + parcelas (`resolveOperationTerm`, DateFormat.ts:108-122). */
  paymentPeriodicity: z.looseObject({ every: z.number().nullish(), periodicity: nullishString }).nullish(),
  periodicity: nullishString,
  // "Resultados da operação" (AmortizationQuery.tsx): taxas em fração, valores em centavos.
  monthlyInterest: z.number().nullish(),
  yearlyInterest: z.number().nullish(),
  effectiveMonthlyCost: z.number().nullish(),
  effectiveYearlyCost: z.number().nullish(),
  comm: cents.nullish(),
  commTc: cents.nullish(),
  commNonTc: cents.nullish(),
  finTax: cents.nullish(),
  paymentFixedCosts: cents.nullish(),
  includePaymentFixedCosts: z.boolean().nullish(),
  indexer: nullishString,
  indexerValue: z.number().nullish(),
  // "Parâmetros da operação" (OperationTab.tsx:425-860).
  numberOfInterestPayments: z.number().nullish(),
  calculationType: nullishString,
  absAmortizationInMonths: z.number().nullish(),
  absInterestInMonths: z.number().nullish(),
  daysInYear: z.number().nullish(),
  paymentDay: z.number().nullish(),
  financeTaxExempted: z.boolean().nullish(),
  fiduciaryGuarantee: z.number().nullish(),
  dueDateOnBusinessDays: z.boolean().nullish(),
  agentCommission: z.looseObject({ amount: z.number().nullish(), type: nullishString }).nullish(),
});

/** `LiquidationScheduleReadModel`, só o que as regras de ação leem. */
export const liquidationScheduleItemSchema = z.looseObject({
  id: nullishString,
  status: nullishString,
  linkStatus: nullishString,
  type: nullishString,
  informedDueDate: nullishString,
});
export type LiquidationScheduleItem = z.infer<typeof liquidationScheduleItemSchema>;

/**
 * `IWarrantyReadModel` (services/creditNote/types/WarrantyModel/warrantyReadModel.ts:15-139). A aba
 * Garantias mostra o rótulo de `warrantyType` e a descrição montada dos campos de cada tipo
 * (`mapTitleWarranty`/`mapDescriptionWarranty`); os campos por tipo passam pelo `looseObject` e são
 * lidos em `credit-note.warranty.ts`.
 */
export const warrantySchema = z.looseObject({
  warrantyType: nullishString,
});

/** `CreditNoteReadModel` (services/creditNote/types/creditNoteReadModel.ts:18-93). */
export const creditNoteSchema = z.looseObject({
  id: z.string(),
  creditNoteNo: z.number(),
  tenant: nullishString,
  tenantDisplay: nullishString,
  status: creditNoteStatusSchema,
  statusDisplay: nullishString,
  productId: nullishString,
  productIdDisplay: nullishString,
  productCategoryDisplay: nullishString,
  productInstrumentType: nullishString,
  fundId: nullishString,
  fundIdDisplay: nullishString,
  personId: nullishString,
  personDisplay: z.string().nullish().meta({ pii: 'name' }),
  personRegistrationNumber: z.string().nullish().meta({ pii: 'document' }),
  personEmail: z.string().nullish().meta({ pii: 'email' }),
  personDiscriminator: z.enum(['NaturalPerson', 'LegalPerson']).nullish(),
  /** A aba Crédito lê `discriminator ?? personDiscriminator` e `personIdDisplay ?? personDisplay` (CreditNoteTabContainer.tsx:46-50). */
  discriminator: nullishString,
  personIdDisplay: z.string().nullish().meta({ pii: 'name' }),
  /** CPF do tomador que as consultas de margem usam (CreditTabContainer.tsx:45-49; DataPrevMarginQuery/index.tsx:26-30). */
  registrationNumber: z.string().nullish().meta({ pii: 'document' }),
  hasPersonDetailsDivergence: z.boolean().nullish(),
  beneficiaryId: nullishString,
  beneficiaryDisplay: z.string().nullish().meta({ pii: 'name' }),
  liquidationMode: nullishString,
  liquidationModeDisplay: nullishString,
  liquidationType: nullishString,
  liquidationClosureStatus: nullishString,
  liquidationClosureStatusDisplay: nullishString,
  /** Em liquidação simples a CreditAPI manda `null` por item (MapperProfile.cs:2356-2360): leia por `liquidationScheduleOf`. */
  liquidationSchedule: z.array(liquidationScheduleItemSchema.nullable()).nullish(),
  transitoryBankAccountId: nullishString,
  transitoryBankAccountOpenAccount: z.boolean().nullish(),
  bankAccountId: nullishString,
  changeBankAccountId: nullishString,
  amortization: amortizationSchema.nullish(),
  paymentScheduleItems: z.array(paymentScheduleItemSchema).nullish(),
  customPaymentPlan: z.boolean().nullish(),
  relatedPersonCreditNote: z.array(relatedPersonSchema).nullish(),
  uploads: z.array(uploadSchema).nullish(),
  timeline: z.array(timelineItemSchema).nullish(),
  acceptanceDate: nullishString,
  blockedUntil: nullishString,
  observations: nullishString,
  warranty: z.array(warrantySchema).nullish(),
  collateral: nullishString,
  liquidationTypeDisplay: nullishString,
  beneficiaryRegistrationNumber: z.string().nullish().meta({ pii: 'document' }),
  installmentId: nullishString,
  insurance: z.boolean().nullish(),
  createdAt: nullishString,
  updatedAt: nullishString,
  createdBy: actorSchema.nullish(),
  updatedBy: actorSchema.nullish(),
  ownerUser: actorSchema.nullish(),
  ownerGroup: ownerGroupSchema.nullish(),
});
export type CreditNote = z.infer<typeof creditNoteSchema>;

/** `GetListApiResponseSuccess<CreditNoteReadModel>` (discovery/02 §7.2). */
export const creditNoteListSchema = z.looseObject({
  data: z.array(creditNoteSchema),
  page: z.number(),
  totalItems: z.number(),
  totalPages: z.number(),
  paginationToken: z.string().nullish(),
});
export type CreditNoteList = z.infer<typeof creditNoteListSchema>;

/** `CreditProductReadModel`, só o que a barra de ações e o cancelar leem (§3.2, legenda). */
export const creditProductSchema = z.looseObject({
  id: z.string(),
  name: nullishString,
  instrumentType: nullishString,
  amortizationType: nullishString,
  categoryName: nullishString,
  warrantyRegistrationOffice: nullishString,
  manualWarranty: z.boolean().nullish(),
  complianceApprovalTenantName: nullishString,
  liquidationApprovalTenantName: nullishString,
  useReleaseToPaymentTrigger: z.boolean().nullish(),
  openBankAccount: z.boolean().nullish(),
  useCollateral: z.boolean().nullish(),
  // As abas Informações e Operação (InformationTab.tsx:118-119, OperationTab.tsx:126-192).
  isConsumerCreditNote: z.boolean().nullish(),
  allowFinanceTaxExemption: z.boolean().nullish(),
  paymentFixedCosts: z.number().nullish(),
  // "Enviar para carteira" (InstallmentCreditNoteSchema.ts:14-20; `walletCode?: string`, creditProductReadModel.ts:171-173; a API manda número).
  walletCode: z.union([z.string(), z.number()]).nullish(),
  billingSettingId: nullishString,
  communicationSettingId: nullishString,
  // A Consignação para compra (SelectConsignmentForm.tsx:87-95; Consignment.tsx:37-38; GovernmentDepartmentsReadModel).
  governmentDepartments: z
    .array(z.looseObject({ id: z.string(), name: nullishString, internalId: nullishString, paymentTransferDay: z.number().nullish(), warrantyRegistrationCutoffDay: z.number().nullish() }))
    .nullish(),
  payrollAgreement: z.looseObject({ requiresPasswordForFreeMarginQuery: z.boolean().nullish() }).nullish(),
});
export type CreditProduct = z.infer<typeof creditProductSchema>;

/** `GET /RejectionReason` → `ComplianceDisapproveReasonType[]`; `OUTROS_MOTIVOS` revela o texto livre. */
export const rejectionReasonSchema = z.looseObject({
  code: z.string(),
  description: z.string(),
  index: z.union([z.string(), z.number()]).nullish(),
});
export const rejectionReasonListSchema = z.array(rejectionReasonSchema);
export type RejectionReason = z.infer<typeof rejectionReasonSchema>;
export const OTHER_REASONS_CODE = 'OUTROS_MOTIVOS';

/** `GET /complianceChecklist/verifyPerson/{personId}` (`AttributeTypeChecklist`). */
export const complianceChecklistItemSchema = z.looseObject({
  id: z.string(),
  attributeType: z.string(),
  attributeValue: z.string().nullish().meta({ pii: 'document' }),
  message: nullishString,
  recordType: nullishString,
  recordId: nullishString,
});
export const complianceChecklistSchema = z.array(complianceChecklistItemSchema);
export type ComplianceChecklistItem = z.infer<typeof complianceChecklistItemSchema>;

/** `CreditNoteWorflowType` (creditNoteReadModel.ts:349-354); status pending/ok/nok/timeout/error. */
export const workflowSchema = z.looseObject({
  id: z.string(),
  status: z.string(),
  workflow: z.string(),
  createdAt: nullishString,
});
export const workflowListSchema = z.array(workflowSchema);

/** Comentário (`IGetCommentCreditNoteReturn`, creditNoteActions.interface.ts:115). */
export const commentSchema = z.looseObject({
  id: nullishString,
  creditNoteId: nullishString,
  comment: z.string(),
  createdAt: nullishString,
  createdBy: z.looseObject({ userIdDisplay: nullishString }).nullish(),
});
export const commentPageSchema = z.looseObject({
  data: z.array(commentSchema),
  page: z.number(),
  totalItems: z.number(),
  totalPages: z.number(),
});

/** `GET /NaturalPerson/{id}` ou `GET /LegalPerson/{id}`: a aba Documentos só lê nome e `uploads` (UploadWithRelatedContainer.tsx:78-110). */
export const personRecordSchema = z.looseObject({
  id: z.string(),
  name: z.string().nullish().meta({ pii: 'name' }),
  uploads: z.array(uploadSchema).nullish(),
});
export type PersonRecord = z.infer<typeof personRecordSchema>;

/** `GET /Enum/FileType` (enumContext.tsx:27-80): `{ name, value, valueAsInt }`, o rótulo do tipo de documento. */
export const fileTypeSchema = z.looseObject({ name: z.string(), value: z.union([z.string(), z.number()]) });
export const fileTypeListSchema = z.array(fileTypeSchema);

/** `SignaturesType` (aba Assinaturas, `GET /CreditNote/{id}/SignUrl`). */
export const signatureUrlSchema = z.looseObject({
  creditNoteId: nullishString,
  personId: nullishString,
  displayName: z.string().nullish().meta({ pii: 'name' }),
  personEmail: z.string().nullish().meta({ pii: 'email' }),
  personCellPhone: z.string().nullish().meta({ pii: 'phone' }),
  url: nullishString,
  createdAt: nullishString,
});
export const signatureUrlListSchema = z.array(signatureUrlSchema);

/** `CosifReadModel` (genericTypes.ts:127-135): conta de débito/crédito na liquidação. */
export const cosifAccountSchema = z.looseObject({
  code: z.string(),
  description: z.string(),
  externalAccountId: nullishString,
});
export const cosifAccountListSchema = z.array(cosifAccountSchema);

/** `CheckAverbationResponseType` (genericTypes.ts:121-125). */
export const checkAverbationSchema = z.looseObject({
  status: nullishString,
  identificador: nullishString,
  protocolo: z.union([z.number(), z.string()]).nullish(),
});

/**
 * Item de `GET /{recordType}/{id}/Dataset[/{nome}]`: a resposta é uma lista e a tela acha o item pelo
 * `dataSet` (`compliance_analysis`, `credit_analysis`). `result` é livre por dataset.
 */
export const datasetItemSchema = z.looseObject({
  dataSet: z.string(),
  status: nullishString,
  result: z.unknown(),
  createdAt: nullishString,
  updatedAt: nullishString,
});
export const datasetListSchema = z.array(datasetItemSchema);
export type DatasetItem = z.infer<typeof datasetItemSchema>;

/**
 * Item de consulta sem `dataSet` garantido: tokens de averbação (`…/dataset/ReserveToken`) e a margem do
 * Crédito do Trabalhador (`GET /DataprevEmployee/FreeMarginQuery`, `IDataprevMarginQueryReadModel`).
 */
export const queryItemSchema = z.looseObject({
  status: nullishString,
  result: z.unknown(),
  createdAt: nullishString,
  updatedAt: nullishString,
});
export const queryListSchema = z.array(queryItemSchema);
export type QueryItem = z.infer<typeof queryItemSchema>;

/** Token de consignado (`GET /NaturalPerson/{id}/dataset/AvailableReserveToken`), aba Tokens. */
export const reserveTokenSchema = z.looseObject({
  tokenType: nullishString,
  tokenValue: nullishString,
  status: nullishString,
  expirationDate: nullishString,
});
export const reserveTokenListSchema = z.array(reserveTokenSchema);

/** Item de `GET /CreditNote/{id}/LiquidationScheduleCreditNote` (drawer de roteiro). */
export const liquidationSchedulePageSchema = z.looseObject({
  data: z.array(liquidationScheduleItemSchema),
  page: z.number(),
  totalItems: z.number(),
  totalPages: z.number(),
});

// ─── corpos (o que o legado manda) ──────────────────────────────────────────

export const insertBlockSchema = z.enum(['Person', 'Operation']).nullable();

/** `POST /disapprove` do detalhe (RejectFormContainer.tsx:52-60). */
export const disapproveBodySchema = z.object({
  message: z.string().nullish(),
  insertBlock: insertBlockSchema,
  blockUntil: z.string().nullish(),
  attributeTypes: z.array(z.string()).nullish(),
  rejectionReasonCode: z.array(z.string()).nullish(),
});
export type DisapproveBody = z.infer<typeof disapproveBodySchema>;

/** `POST /disapprove` da rejeição em lote na lista (CreditNoteRejectComplianceContainer.tsx:36-43). */
export const listDisapproveBodySchema = z.object({
  rejectionReasonCode: z.array(z.string()).nullish(),
  message: z.string(),
  insertBlock: insertBlockSchema,
  blockUntil: z.string(),
});
export type ListDisapproveBody = z.infer<typeof listDisapproveBodySchema>;

/** `POST /cancel` (CancelFormContainer.tsx:54-60). */
export const cancelBodySchema = z.object({
  message: z.string(),
  insertBlock: insertBlockSchema,
  blockUntil: z.string().nullish(),
  rejectionReasonCode: z.array(z.string()),
  attributeTypes: z.array(z.string()).nullish(),
});
export type CancelBody = z.infer<typeof cancelBodySchema>;

/**
 * `POST /cancel` do lote na lista: o formulário cru (`onCancel(formValues)`, creditNoteListActions.tsx:220-224,
 * defaults de CancelSchema.ts:32-38), com `complianceChecklist` e sem `attributeTypes`.
 */
export const listCancelBodySchema = z.object({
  message: z.string(),
  insertBlock: insertBlockSchema,
  blockUntil: z.string().nullable(),
  rejectionReasonCode: z.array(z.string()),
  complianceChecklist: z.array(z.string()),
});
export type ListCancelBody = z.infer<typeof listCancelBodySchema>;

/** `POST /approveLiquidation` do detalhe (ApproveLiquidationContainer.tsx:49-53). */
export const approveLiquidationBodySchema = z.object({
  CosifAccountCode: z.string(),
  IsAutomaticBankTransfer: z.boolean(),
  creditAccountId: z.string().nullish(),
});
export type ApproveLiquidationBody = z.infer<typeof approveLiquidationBodySchema>;

/** Corpo de bloqueio que o `releaseToPayment` põe em `Message` quando `action === 'Cancel'`. */
export const releaseToPaymentMessageSchema = z.object({
  message: z.string().nullish(),
  insertBlock: insertBlockSchema.optional(),
  blockUntil: z.string().nullish(),
  rejectionReasonCode: z.array(z.string()).nullish(),
  complianceChecklist: z.array(z.string()).nullish(),
});
export const releaseToPaymentActionSchema = z.enum(['Release', 'Cancel', 'Sign']);

/** Etapas de "Reiniciar Esteira" (ReprocessByStatusSchema; discovery/02 §4.2 A14). */
export const reprocessStepSchema = z.enum(['All', 'Compliance', 'CreditApproval', 'Signature', 'Warranty', 'Liquidation', 'MarginReserve', 'SignatureValidation']);
export type ReprocessStep = z.infer<typeof reprocessStepSchema>;

/** `POST /manualUpdate` (ManualUpdate.tsx:28-36): `sendWebCred` sai do corpo e vira outra chamada. */
export const manualUpdateBodySchema = z.object({
  newStatus: z.string(),
  timelineAction: z.enum(['None', 'EndCurrent', 'EndAndCreateNew']).nullish(),
  newTimelineType: z.string().nullish(),
  newTimelineDescription: z.string().nullish(),
  workflowAction: z.enum(['None', 'EndCurrent', 'EndAndCreateNew']).nullish(),
  acceptanceDate: z.string().nullish(),
});
export type ManualUpdateBody = z.infer<typeof manualUpdateBodySchema>;

/** `UploadUpdateModel` (listUploadModel.ts:3-12): as 4 chaves que `toDataModel` mantém. */
export const uploadUpdateSchema = z.object({
  id: z.string().optional(),
  fileType: z.string(),
  fileName: z.string(),
  displayName: z.string(),
});
export type UploadUpdate = z.infer<typeof uploadUpdateSchema>;

/** Token de garantia/margem (`approveWarranty`, `approveMarginReserve`; discovery/02 §4.3). */
export const warrantyTokenSchema = z.object({
  token: z.object({ tokenType: z.string(), tokenValue: z.string() }),
  warrantyRegistrationOffice: z.string().nullish(),
  expirationDate: z.string().nullish(),
  warrantyCode: z.string().nullish(),
});

// ─── opções dos filtros da lista (autocompletes do legado) ──────────────────

/** Item de `GET /Product` como o autocomplete do filtro usa (`useCreditProdutList`). */
export const productOptionSchema = z.looseObject({ id: z.string(), name: nullishString, tenantDisplay: nullishString, categoryName: nullishString });
/** Item de `GET /Person` como o autocomplete do filtro usa (`usePersonsList`). */
export const personOptionSchema = z.looseObject({
  id: z.string(),
  name: z.string().nullish().meta({ pii: 'name' }),
  registrationNumber: z.string().nullish().meta({ pii: 'document' }),
  discriminator: nullishString,
  tenantDisplay: nullishString,
});
/** Item de `GET /Fund` como o autocomplete do filtro usa (`useFundsList`). */
export const fundOptionSchema = z.looseObject({ id: z.string(), name: nullishString, tenantDisplay: nullishString });

const optionPage = <T extends z.ZodType>(item: T) => z.looseObject({ data: z.array(item), page: z.number().nullish(), totalItems: z.number().nullish(), totalPages: z.number().nullish() });
export const productOptionPageSchema = optionPage(productOptionSchema);
export const personOptionPageSchema = optionPage(personOptionSchema);
export const fundOptionPageSchema = optionPage(fundOptionSchema);
export type ProductOption = z.infer<typeof productOptionSchema>;
export type PersonOption = z.infer<typeof personOptionSchema>;
export type FundOption = z.infer<typeof fundOptionSchema>;

// ─── ações da barra do detalhe (trilha "barra") ─────────────────────────────

/**
 * `IReserveTokensRegistered` (naturalPersonReadModel.ts:123-139) de `GET /NaturalPerson/{id}/dataset/AvailableReserveToken`:
 * o token mora em `result`, em PascalCase (`useGetAvailableReserveTokenByPersonId`, naturalPersonContext.tsx:248-259).
 */
export const registeredReserveTokenSchema = z.looseObject({
  status: nullishString,
  createdAt: nullishString,
  createdBy: z.looseObject({ userIdDisplay: nullishString }).nullish(),
  result: z.looseObject({
    WarrantyRegistrationOffice: nullishString,
    WarrantyRegistrationOfficeDisplay: nullishString,
    ExpirationDate: nullishString,
    WarrantyCode: nullishString,
    Token: z.looseObject({ TokenType: nullishString, TokenTypeDisplay: nullishString, TokenValue: z.string() }),
  }),
});
export const registeredReserveTokenListSchema = z.array(registeredReserveTokenSchema);
export type RegisteredReserveToken = z.infer<typeof registeredReserveTokenSchema>;

/** `ReserveTokenCreateModel` (WarrantyRegistrationOfficeToken.ts:1-15): o corpo do "Adicionar token consignado". */
export const reserveTokenCreateSchema = z.object({
  warrantyRegistrationOffice: z.string(),
  token: z.object({ tokenType: z.string(), tokenValue: z.string() }),
  expirationDate: z.string(),
  warrantyCode: z.string().optional(),
});
export type ReserveTokenCreate = z.infer<typeof reserveTokenCreateSchema>;

/** Corpo do `POST /{recurso}/{id}/Share` (`ShareRecordRequestType`, services/share/types/share.ts). */
export const shareBodySchema = z.object({
  permissionLevelType: z.string().nullable(),
  principalId: z.string().nullable(),
  principalType: z.string().nullable(),
});
export type ShareBody = z.infer<typeof shareBodySchema>;

/** `AssingCreateModel` (services/assign/types/AssingCreateModel.ts): `toDataModel` guarda `userId`, `groupName`, `tenant`. */
export const assignBodySchema = z.object({
  userId: z.string().nullish(),
  groupName: z.string().nullish(),
  tenant: z.string().optional(),
});
export type AssignBody = z.infer<typeof assignBodySchema>;

/** `ISemearProposalStatusResponse` de `POST /DataprevInss/Semear/Status/{id}`: o toast lê `data[0].description`. */
export const semearStatusListSchema = z.array(z.looseObject({ description: nullishString }));

/** `GetPublicPayrollLoanReadModel` (payrollAgreement/types): as operações do "Calendário de troco". */
export const publicPayrollLoanSchema = z.looseObject({
  id: z.string(),
  creditNoteNo: z.number().nullish(),
  personDisplay: z.string().nullish().meta({ pii: 'name' }),
  statusDisplay: nullishString,
  tenantDisplay: nullishString,
});
export const publicPayrollLoanPageSchema = z.looseObject({ data: z.array(publicPayrollLoanSchema) });
export type PublicPayrollLoan = z.infer<typeof publicPayrollLoanSchema>;

/** `CalculateDuePaymentCreateModel` (calculateDueCreateModel.ts:1-9): as 3 chaves que `toDataModel` guarda. */
export const duePaymentBodySchema = z.object({ creditNoteId: z.string(), referenceDate: z.string(), numberOfDays: z.number() });
export type DuePaymentBody = z.infer<typeof duePaymentBodySchema>;

/** `CalculateDuePaymentResponse` (calculateDueCreateModel.ts:10-15): valores em centavos. */
export const duePaymentItemSchema = z.looseObject({ liquidationDate: nullishString, dueValue: z.number().nullish(), liquidValue: z.number().nullish(), changeValue: z.number().nullish() });
export const duePaymentListSchema = z.array(duePaymentItemSchema);
export type DuePaymentItem = z.infer<typeof duePaymentItemSchema>;

// ─── escrita nas abas do detalhe (trilha "abas") ────────────────────────────

/** `EmploymentStatusReadModel` de `GET /EmploymentStatus` (employmentStatusContext.tsx:47-55): a situação do vínculo SIAPE. */
export const employmentStatusPageSchema = z.looseObject({
  data: z.array(z.looseObject({ code: z.union([z.string(), z.number()]), name: z.string().nullish() })),
});

/** `GET /LegalPerson/{id}`: os signatários da PJ que o drawer "Envolvido" importa (InvolvedTabContainer.tsx:104-107, 208-219). */
export const legalPersonSignatoriesSchema = z.looseObject({
  id: z.string(),
  name: z.string().nullish().meta({ pii: 'name' }),
  relatedPerson: z.array(relatedPersonSchema).nullish(),
});
export type LegalPersonSignatories = z.infer<typeof legalPersonSignatoriesSchema>;

